/**
 * Exam URL slug helpers for SEO-friendly routes: /exams/telc-b1, /exams/goethe-b2, etc.
 * Slug format: {provider}-{level} (lowercase). Used for routing and access control.
 */

const PROVIDERS = ['telc', 'goethe', 'osd'];
const LEVELS = ['b1', 'b2', 'c1'];

/** All valid exam slugs (lowercase). */
export const VALID_EXAM_SLUGS = PROVIDERS.flatMap((p) =>
  LEVELS.map((l) => `${p}-${l}`)
);

/**
 * @param {string} provider - e.g. 'TELC', 'Goethe', 'ÖSD'
 * @param {string} level - e.g. 'B1', 'B2', 'C1'
 * @returns {string} e.g. 'telc-b1'
 */
export function getExamSlug(provider, level) {
  if (!provider || !level) return '';
  const p = String(provider).toLowerCase().trim();
  const l = String(level).toUpperCase().toLowerCase();
  return `${p}-${l}`;
}

/**
 * @param {string} slug - e.g. 'telc-b1'
 * @returns {{ provider: string, level: string } | null} Provider/level for examService (e.g. TELC, B1)
 */
export function parseExamSlug(slug) {
  if (!slug || typeof slug !== 'string') return null;
  const normalized = slug.toLowerCase().trim();
  if (!VALID_EXAM_SLUGS.includes(normalized)) return null;
  const [provider, level] = normalized.split('-');
  return {
    provider: provider.toUpperCase(),
    level: level.toUpperCase(),
  };
}

/**
 * Check if slug is valid.
 * @param {string} slug
 * @returns {boolean}
 */
export function isValidExamSlug(slug) {
  return Boolean(slug && VALID_EXAM_SLUGS.includes(String(slug).toLowerCase().trim()));
}

/**
 * Get the exam slug that the user is allowed to access (from profile/exam selection).
 * @param {{ examProvider?: string, level?: string } | null} examSelection - from userService.getExamSelection()
 * @returns {string} slug or ''
 */
export function getAllowedExamSlug(examSelection) {
  if (!examSelection?.examProvider || !examSelection?.level) return '';
  return getExamSlug(examSelection.examProvider, examSelection.level);
}
